/**
 * Files still on their way into each deal (DOP-608), so a row appears the
 * moment they are added rather than only once the whole upload lands. Shared
 * by the sidebar's Deal Room and the Deal Room pages.
 *
 * Paths the deal already holds are left to their own row. Per job, the paths
 * the tree has already shown are remembered: once a file has arrived, its
 * pending row never comes back — even if the file is deleted later.
 */
import { useMemo, useRef } from "react";
import type { PendingUpload } from "../components/AddFiles/DealFileStatus";
import { useUploadManager } from "../context/UploadManager";
import type { FolderTreeNode } from "../services/fileApi";
import { flattenDealFiles } from "../utils/addFilesReview";
import { relativePathFor } from "../utils/folderUpload";

export function usePendingUploads(
	roots: FolderTreeNode[],
): Map<number, PendingUpload[]> {
	const { jobs } = useUploadManager();
	const arrivedPathsRef = useRef(new Map<string, Set<string>>());
	return useMemo(() => {
		const byFolder = new Map<number, PendingUpload[]>();
		const stored = new Map(
			roots.map((root) => [root.folder.id, flattenDealFiles(root)]),
		);
		for (const job of jobs) {
			// A finished job's files stay listed until the refreshed tree has
			// them — otherwise the row blinks out between "stored" and the tree
			// re-read landing. Files the server refused never arrive; skip those.
			const finished = job.status === "done" || job.status === "partial";
			if (job.status !== "queued" && job.status !== "uploading" && !finished) {
				continue;
			}
			const notStored = new Set([
				...(job.result?.failed ?? []),
				...(job.result?.notAttempted ?? []),
			]);
			const inDeal = stored.get(job.folderId);
			const arrived =
				arrivedPathsRef.current.get(job.id) ?? new Set<string>();
			arrivedPathsRef.current.set(job.id, arrived);
			const list = byFolder.get(job.folderId) ?? [];
			for (const file of job.files) {
				const path = relativePathFor(file);
				if (inDeal?.has(path)) {
					arrived.add(path);
					continue;
				}
				if (arrived.has(path)) continue;
				if (finished && notStored.has(path)) continue;
				if (list.some((item) => item.path === path)) continue;
				list.push({
					path,
					name: path.split("/").pop() ?? path,
					waiting: job.status === "queued",
				});
			}
			if (list.length > 0) byFolder.set(job.folderId, list);
		}
		return byFolder;
	}, [jobs, roots]);
}
